import React, { useMemo } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/common/reducers/hooks/store.hook';
import { makeSelectNameForDisplay, selectReplyingToMessage } from './redux/TeamInboxSelectors';
import { TeamInboxActions } from './redux/TeamInboxReducer';
import { getSenderColor, mediaTypeLabel } from './team-inbox-helpers';

/**
 * WhatsApp-style reply preview bar shown ABOVE the composer while a reply is
 * staged: rounded panel, 4px colored start bar, author line ('You' for own
 * messages), one-line snippet or media label, and an X to cancel. Renders
 * nothing when no reply is staged. RTL-safe (logical properties).
 */
const ReplyPreviewBar: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const replyingTo = useAppSelector(selectReplyingToMessage);

    const fromMe = !!replyingTo?.fromMe;
    // Group messages carry author; 1:1 received messages fall back to the chat peer.
    const senderId: string | undefined = replyingTo?.author?._serialized
        ?? replyingTo?.author
        ?? replyingTo?.from
        ?? replyingTo?.chatId;

    const selectSenderName = useMemo(
        () => (!fromMe && senderId
            ? makeSelectNameForDisplay(senderId, replyingTo?.senderName ?? replyingTo?.notifyName)
            : () => ''),
        [fromMe, senderId, replyingTo?.senderName, replyingTo?.notifyName],
    );
    const senderName = useAppSelector(selectSenderName);

    if (!replyingTo) return null;

    const authorLabel = fromMe ? t('you', { defaultValue: 'You' }) : (senderName || '');
    const barColor = fromMe
        ? 'var(--wa-accent, #00a884)'
        : (getSenderColor(authorLabel || senderId || 'peer') || 'var(--wa-accent, #00a884)');
    const snippet = (replyingTo.body || replyingTo.caption || '').trim()
        || mediaTypeLabel(replyingTo.type)
        || '';

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px 0 16px',
            backgroundColor: 'var(--wa-panel-bg, #f0f2f5)',
        }}>
            <Box sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'stretch',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: 'var(--wa-quote-bg, rgba(0,0,0,0.06))',
            }}>
                <Box sx={{ width: '4px', flexShrink: 0, backgroundColor: barColor }} />
                <Box sx={{ px: '10px', py: '6px', minWidth: 0, flex: 1 }}>
                    <Typography component="div" sx={{
                        fontSize: '12.5px',
                        fontWeight: 500,
                        lineHeight: '17px',
                        color: barColor,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {authorLabel}
                    </Typography>
                    <Typography component="div" dir="auto" sx={{
                        fontSize: '13px',
                        lineHeight: '18px',
                        color: 'var(--wa-meta-text, #667781)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {snippet}
                    </Typography>
                </Box>
            </Box>
            <IconButton
                size="small"
                aria-label={t('teamInbox.cancelReply', { defaultValue: 'Cancel reply' })}
                onClick={() => dispatch(TeamInboxActions.setReplyingToMessage(null))}
                sx={{ color: 'var(--wa-secondary-text, #54656f)', flexShrink: 0 }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

export default ReplyPreviewBar;
